import React, { useCallback, useEffect, useMemo, useState } from "react";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import "./auditoria-fiserv.css";

// Esta vista responde "que trajo Fiserv", no "con que Clover hizo match". La fila existe
// aunque el matcher no la haya visto, asi que no se usa ningun dato de Clover salvo una
// columna de estado, secundaria y opcional.

export const TABS = [
  { id: "TODAS", label: "Todas" },
  { id: "VISA_DEBITO", label: "Visa débito" },
  { id: "MASTERCARD_DEBITO", label: "Mastercard débito" },
  { id: "MAESTRO", label: "Maestro" },
  { id: "VISA", label: "Visa" },
  { id: "MASTERCARD", label: "Mastercard" },
  { id: "AMEX", label: "Amex" },
  { id: "CABAL", label: "Cabal" },
  { id: "QR", label: "QR" },
  { id: "NARANJA", label: "Naranja" },
  { id: "OTRAS", label: "Otras" },
];

const SUBFILTROS = [
  { id: "", label: "Todas" },
  { id: "DEBIT", label: "Débito" },
  { id: "CREDIT_ONE_PAYMENT", label: "1 pago" },
  { id: "CREDIT_INSTALLMENTS", label: "Cuotas" },
];

const COLUMNAS_ORDENABLES = [
  { id: "fechaoperacion", label: "Fecha operación" },
  { id: "fechapago", label: "Fecha pago" },
  { id: "comercio", label: "Comercio" },
  { id: "cupon", label: "Cupón" },
  { id: "terminal", label: "Terminal" },
  { id: "importe", label: "Importe" },
  { id: "tarjeta", label: "Tarjeta" },
  { id: "cuotas", label: "Cuotas" },
];

const TAMANOS = [50, 100, 250, 500];

export const formatearImporte = (valor) =>
  typeof valor === "number"
    ? valor.toLocaleString("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 })
    : "—";

export const formatearFecha = (valor) => {
  if (!valor) return "—";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "—";
  const dd = String(fecha.getDate()).padStart(2, "0");
  const mm = String(fecha.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${fecha.getFullYear()}`;
};

// La columna "anticipo%" del conversor. Sin tasa se muestra un guion, no 0 %: un cero
// inventado se lee como "sin anticipo", que es distinto de "no sabemos".
export const formatearTasa = (valor) =>
  typeof valor === "number"
    ? `${(valor * 100).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
    : "—";

export const describirRango = (page, pageSize, total) => {
  if (!total) return "Sin operaciones";
  const desde = (page - 1) * pageSize + 1;
  const hasta = Math.min(page * pageSize, total);
  return `Mostrando ${desde}–${hasta} de ${total} operaciones`;
};

const FILTROS_VACIOS = {
  operationDate: "", paymentDate: "", presentationDate: "",
  commerce: "", terminal: "", coupon: "", batch: "", authorization: "",
  installments: "", tna: "", minAnticipoRate: "", search: "",
};

function Card({ titulo, valor, importe }) {
  return (
    <div className="afi-card" data-testid={`afi-card-${titulo}`}>
      <span className="afi-card__titulo">{titulo}</span>
      <strong className="afi-card__valor">{valor}</strong>
      {importe !== undefined && <span className="afi-card__importe">{formatearImporte(importe)}</span>}
    </div>
  );
}

function Detalle({ fila }) {
  return (
    <tr className="afi-detalle" data-testid={`afi-detalle-${fila.fiservOperationId}`}>
      <td colSpan={18}>
        <div className="afi-detalle__grid">
          <section>
            <h5>Identificación</h5>
            <dl>
              <dt>FiservOperationId</dt><dd>{fila.fiservOperationId}</dd>
              <dt>Archivo</dt><dd>{fila.sourceFile || "—"}</dd>
              <dt>Fila origen</dt><dd>{fila.sourceRow ?? "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Operación</h5>
            <dl>
              <dt>Fecha operación</dt><dd>{formatearFecha(fila.fechaOperacion)}</dd>
              <dt>Fecha presentación</dt><dd>{formatearFecha(fila.fechaPresentacion)}</dd>
              <dt>Fecha de pago</dt><dd>{formatearFecha(fila.fechaPago)}</dd>
              <dt>Comercio</dt><dd>{fila.numeroComercio || "—"}</dd>
              <dt>Cupón</dt><dd>{fila.numeroCupon || "—"}</dd>
              <dt>Lote</dt><dd>{fila.numeroLote || "—"}</dd>
              <dt>Autorización</dt><dd>{fila.numeroAutorizacion || "—"}</dd>
              <dt>Tarjeta</dt><dd>{fila.tarjeta || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Producto</h5>
            <dl>
              <dt>productType</dt><dd>{fila.productType || "—"}</dd>
              <dt>TransactionTypeRaw</dt><dd>{fila.transactionTypeRaw || "—"}</dd>
              <dt>Promoción plan</dt><dd>{fila.promocionPlan || "—"}</dd>
              <dt>Cuotas normalizadas</dt><dd>{fila.rawInstallmentCount ?? "—"}</dd>
              <dt>PlanCuotaOriginal</dt><dd>{fila.planCuotaOriginal ?? "—"}</dd>
              <dt>Plan Fiserv</dt><dd>{fila.fiservPlanCode ?? "—"}</dd>
              <dt>TNA</dt><dd>{fila.tna ?? "—"}</dd>
              <dt>TF</dt><dd>{fila.tfRaw || "—"}</dd>
            </dl>
          </section>
          <section>
            <h5>Financiero</h5>
            <dl>
              <dt>Total bruto</dt><dd>{formatearImporte(fila.totalBruto)}</dd>
              <dt>Dto. Arancel</dt><dd>{formatearImporte(fila.dtoArancel)}</dd>
              <dt>Dto. Financiero</dt><dd>{formatearImporte(fila.dtoFinanciero)}</dd>
              <dt>Anticipo %</dt><dd>{formatearTasa(fila.anticipoRate)}</dd>
              <dt>Total descuento</dt><dd>{fila.totalDescuento ?? "—"}</dd>
              <dt>Total neto</dt><dd>{formatearImporte(fila.totalNeto)}</dd>
            </dl>
          </section>
        </div>
      </td>
    </tr>
  );
}

export default function AuditoriaFiserv({ runId, token, darkMode }) {
  const [datos, setDatos] = useState(null),
    [cargando, setCargando] = useState(false),
    [error, setError] = useState(""),
    [colapsado, setColapsado] = useState(true),
    [tab, setTab] = useState("TODAS"),
    [producto, setProducto] = useState(""),
    [filtros, setFiltros] = useState(FILTROS_VACIOS),
    [orden, setOrden] = useState({ by: "fechaoperacion", desc: false }),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(100),
    [abierta, setAbierta] = useState("");

  const consulta = useMemo(() => {
    const { minAnticipoRate, ...resto } = filtros;
    return {
      tab, productType: producto, ...resto,
      // En pantalla se escribe 7 y se manda 0.07: nadie piensa el anticipo en tasa.
      minAnticipoRate: minAnticipoRate === "" ? "" : Number(minAnticipoRate) / 100,
      sortBy: orden.by, sortDescending: orden.desc, page, pageSize,
    };
  }, [tab, producto, filtros, orden, page, pageSize]);

  const cargar = useCallback(async () => {
    if (!runId || colapsado) return;
    setCargando(true);
    setError("");
    try {
      setDatos(await api.obtenerOperacionesFiserv(runId, token, consulta));
    } catch (e) {
      setDatos(null);
      setError(e?.message || "No se pudo cargar la auditoría Fiserv.");
    } finally {
      setCargando(false);
    }
  }, [runId, token, consulta, colapsado]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { setPage(1); }, [tab, producto, filtros, pageSize]);
  useEffect(() => { setDatos(null); setTab("TODAS"); setFiltros(FILTROS_VACIOS); }, [runId]);

  const cambiarFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));
  const ordenarPor = (campo) =>
    setOrden((prev) => ({ by: campo, desc: prev.by === campo ? !prev.desc : false }));

  const filas = datos?.rows || [];
  const s = datos?.filteredSummary;
  const g = datos?.goldenComparison;
  const tabs = datos?.tabs || [];
  const cuentaDe = (id) => tabs.find((t) => t.tab === id)?.operations ?? 0;

  if (!runId) return null;

  return (
    <section className={`afi ${darkMode ? "afi--dark" : ""}`} data-testid="auditoria-fiserv">
      <header className="afi__header">
        <div>
          <h3>Auditoría de operaciones Fiserv</h3>
          <p className="afi-nota">Vista equivalente al SAS del conversor</p>
        </div>
        <button
          type="button"
          className="afi__toggle"
          onClick={() => setColapsado((v) => !v)}
          data-testid="afi-toggle"
          aria-expanded={!colapsado}
        >
          {colapsado ? "Mostrar detalle" : "Ocultar detalle"}
        </button>
        {!colapsado && (
          <a
            className="afi__export"
            href={api.buildExportOperacionesFiservUrl(runId, token, consulta)}
            data-testid="afi-export"
          >
            Exportar vista
          </a>
        )}
      </header>

      {colapsado && <p className="afi-nota">Bloque colapsado. Abrilo para auditar operación por operación.</p>}

      {!colapsado && (
        <>
          {error && <p className="afi-error" role="alert" data-testid="afi-error">{error}</p>}
          {cargando && !datos && <p data-testid="afi-cargando">Cargando auditoría…</p>}

          {!error && datos && s && (
            <>
              <div className="afi-cards">
                <Card titulo="Operaciones" valor={s.operations} importe={s.grossAmount} />
                <Card titulo="Débito" valor={s.debitCount} importe={s.debitAmount} />
                <Card titulo="Crédito 1 pago" valor={s.creditOnePaymentCount} importe={s.creditOnePaymentAmount} />
                <Card titulo="Cuotas" valor={s.installmentsCount} importe={s.installmentsAmount} />
                <Card titulo="Otros" valor={s.otherCount} importe={s.otherAmount} />
                {g && <Card titulo="Source gaps" valor={g.knownSourceGaps} />}
              </div>

              {g && (
                <div className="afi-golden" data-testid="afi-golden">
                  <strong>Validación conversor</strong>
                  <span>Golden SAS: {g.goldenTotal ?? "—"}</span>
                  <span>Source gaps conocidos: {g.knownSourceGaps}</span>
                  <span>Esperado disponible: {g.expectedFromAvailableSources ?? "—"}</span>
                  <span>Builder: {g.builderTotal}</span>
                  <span className={`afi-estado afi-estado--${g.status === "CUADRA_CON_FUENTES_DISPONIBLES" ? "ok" : "alerta"}`}>
                    {g.status}
                  </span>
                  {g.sourceGaps?.length > 0 && (
                    <span className="afi-nota">
                      ({g.sourceGaps.map((x) => `${x.category}: ${x.goldenOperations}`).join(" · ")})
                    </span>
                  )}
                </div>
              )}

              <nav className="afi-tabs" role="tablist">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    className={tab === t.id ? "activo" : ""}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label} <span className="afi-tabs__cuenta">{cuentaDe(t.id)}</span>
                  </button>
                ))}
              </nav>

              <nav className="afi-subtabs">
                {SUBFILTROS.map((f) => (
                  <button
                    key={f.id || "todas"}
                    type="button"
                    className={producto === f.id ? "activo" : ""}
                    onClick={() => setProducto(f.id)}
                  >
                    {f.label}
                  </button>
                ))}
                {producto === "CREDIT_INSTALLMENTS" && (
                  <input
                    className="afi-cuotas"
                    type="number"
                    min="2"
                    placeholder="cuotas"
                    value={filtros.installments}
                    onChange={(e) => cambiarFiltro("installments", e.target.value)}
                    aria-label="Cantidad de cuotas"
                  />
                )}
              </nav>

              <div className="afi-filtros">
                <label>Fecha operación<input type="date" value={filtros.operationDate} onChange={(e) => cambiarFiltro("operationDate", e.target.value)} /></label>
                <label>Fecha pago<input type="date" value={filtros.paymentDate} onChange={(e) => cambiarFiltro("paymentDate", e.target.value)} /></label>
                <label>Comercio<input value={filtros.commerce} onChange={(e) => cambiarFiltro("commerce", e.target.value)} /></label>
                <label>Terminal<input value={filtros.terminal} onChange={(e) => cambiarFiltro("terminal", e.target.value)} /></label>
                <label>Cupón<input value={filtros.coupon} onChange={(e) => cambiarFiltro("coupon", e.target.value)} /></label>
                <label>Lote<input value={filtros.batch} onChange={(e) => cambiarFiltro("batch", e.target.value)} /></label>
                <label>Autorización<input value={filtros.authorization} onChange={(e) => cambiarFiltro("authorization", e.target.value)} /></label>
                <label>TNA<input value={filtros.tna} onChange={(e) => cambiarFiltro("tna", e.target.value)} placeholder="115" /></label>
                <label>Anticipo desde %<input type="number" step="0.01" value={filtros.minAnticipoRate} onChange={(e) => cambiarFiltro("minAnticipoRate", e.target.value)} placeholder="7" /></label>
                <label className="afi-filtros__buscador">Buscar<input value={filtros.search} onChange={(e) => cambiarFiltro("search", e.target.value)} placeholder="comercio, cupón, lote, autorización…" /></label>
                <button type="button" onClick={() => setFiltros(FILTROS_VACIOS)} data-testid="afi-limpiar">Limpiar</button>
              </div>

              <div className="afi-barra">
                <span data-testid="afi-rango">{describirRango(datos.page, datos.pageSize, datos.filteredCount)}</span>
                <strong>{formatearImporte(datos.filteredGrossAmount)}</strong>
                <label>
                  Filas
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Filas por página">
                    {TAMANOS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <button type="button" disabled={datos.page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
                <button
                  type="button"
                  disabled={datos.page * datos.pageSize >= datos.filteredCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </button>
              </div>

              {filas.length === 0 ? (
                <p className="afi-vacio" data-testid="afi-sin-resultados">No hay operaciones con estos filtros.</p>
              ) : (
                <div className="afi-scroll">
                  <table className="afi-tabla">
                    <thead>
                      <tr>
                        {COLUMNAS_ORDENABLES.slice(0, 1).map((c) => (
                          <th key={c.id}>
                            <button type="button" onClick={() => ordenarPor(c.id)}>{c.label}</button>
                          </th>
                        ))}
                        <th>Presentación</th>
                        <th><button type="button" onClick={() => ordenarPor("fechapago")}>Fecha pago</button></th>
                        <th><button type="button" onClick={() => ordenarPor("cupon")}>Cupón</button></th>
                        <th><button type="button" onClick={() => ordenarPor("comercio")}>Comercio</button></th>
                        <th>Moneda</th>
                        <th className="afi-num"><button type="button" onClick={() => ordenarPor("importe")}>Total bruto</button></th>
                        <th className="afi-num">Total descuento</th>
                        <th className="afi-num">Total neto</th>
                        <th>Liquidación</th>
                        <th>Lote</th>
                        <th>Estado</th>
                        <th><button type="button" onClick={() => ordenarPor("cuotas")}>Cuotas</button></th>
                        <th>Autorización</th>
                        <th><button type="button" onClick={() => ordenarPor("tarjeta")}>Tarjeta</button></th>
                        <th>Producto</th>
                        <th className="afi-num"><button type="button" onClick={() => ordenarPor("tna")}>TNA</button></th>
                        <th className="afi-num"><button type="button" onClick={() => ordenarPor("anticipo")}>Anticipo %</button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((fila) => (
                        <React.Fragment key={fila.fiservOperationId}>
                          <tr
                            className="afi-fila"
                            data-testid={`afi-fila-${fila.fiservOperationId}`}
                            onClick={() => setAbierta(abierta === fila.fiservOperationId ? "" : fila.fiservOperationId)}
                          >
                            <td>{formatearFecha(fila.fechaOperacion)}</td>
                            <td>{formatearFecha(fila.fechaPresentacion)}</td>
                            <td>{formatearFecha(fila.fechaPago)}</td>
                            <td>{fila.numeroCupon || "—"}</td>
                            <td>{fila.numeroComercio || "—"}</td>
                            <td>{fila.moneda || "—"}</td>
                            <td className="afi-num">{formatearImporte(fila.totalBruto)}</td>
                            <td className="afi-num">{fila.totalDescuento ?? "—"}</td>
                            <td className="afi-num">{formatearImporte(fila.totalNeto)}</td>
                            <td>{fila.numeroLiquidacion || "—"}</td>
                            <td>{fila.numeroLote || "—"}</td>
                            <td>{fila.estado || "—"}</td>
                            <td>{fila.rawInstallmentCount ?? "—"}</td>
                            <td>{fila.numeroAutorizacion || "—"}</td>
                            <td>{fila.tarjeta || "—"}</td>
                            <td>{fila.productType || "—"}</td>
                            <td className="afi-num">{fila.tna ?? "—"}</td>
                            <td className="afi-num afi-anticipo">{formatearTasa(fila.anticipoRate)}</td>
                          </tr>
                          {abierta === fila.fiservOperationId && <Detalle fila={fila} />}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
